import React, {Component, ReactElement, RefObject} from 'react';
import ReactDOM from 'react-dom';
import {GridList, GridListTile} from "@material-ui/core";
import {Content} from "../../../model/content";
import HomeSquare from "../home_square/home_square";
import {HOME_CONFIG} from "../../../config/home_page_config";
import { shuffle, range } from 'lodash';
import './home_grid.scss';
import ScrollContainer from 'react-indiana-drag-scroll';
import {hashCode} from "../../../utils/utils";
import {isMobile} from "react-device-detect";


interface HomeGridProps {
    squareSelected: (content: Content) => void
    contents: Content[],
    id?: string,
    firstAnimationStarted: boolean
}

const DOWNWARD_DIRECTION_INDEX = 7;

/**
 * The Squares Grid shown in the Home page with its controls
 *
 * props:
 * - contents: the list of contents to display in the squares
 * */
class HomeGrid extends Component<HomeGridProps, any> {

    private gridRef: RefObject<any> = React.createRef();
    private rootRef: RefObject<any> = React.createRef();
    private fadeRef: RefObject<any> = React.createRef();

    SQUARE_SIZE: number;
    moveIndex: number;
    newKey: number;

    intervalInitialized = false;
    rotated = false;
    prevY = 0;

    moveInterval?: NodeJS.Timeout;
    changeDirectionInterval?: NodeJS.Timeout;
    addToBordersInterval?: NodeJS.Timeout;

    state: any;

    MOVES = [
        [1, 1],
        [1, 0],
        [1, -1],
        [0, -1],
        [-1, -1],
        [-1, 0],
        [-1, 1],
        [0, 1]
    ];

    constructor(props: Readonly<HomeGridProps>) {
        super(props);

        this.SQUARE_SIZE = HOME_CONFIG.DESKTOP_SQUARE_SIZE;

        this.moveIndex = 0;
        this.newKey = 0;

        // Initialize the component state
        this.state = {
            hSquares: HOME_CONFIG.INITIAL_HORIZONTAL_SQUARES,
            vSquares: HOME_CONFIG.INITIAL_VERTICAL_SQUARES,
            firstAnimationStarted: false
        };
    }


    // region: content matrix management, movement and expansion

    /**
     * The method adds a column to the content motrix at the position specified
     *
     * @param matrix - the content matrix, the input will not be updated
     * @param position - the position at which the column will be added
     * @param hSquares - the current number of columns
     * @param vSquares - the current number of rows
     * @returns the content matrix with the new column
     */
    addColumnToMatrix(matrix: ReactElement[][], position: number,
                      hSquares: number, vSquares: number) {
        let prevMatrix = matrix.slice();

        // Add a column at the end of the content matrix (it will be displayed on the right)
        let contentsLine = shuffle(this.props.contents);
        prevMatrix = prevMatrix.map((row, rowIndex) => {
            row.splice(position, 0, this.squareComponent(contentsLine[rowIndex % contentsLine.length]));
            // Remove the last column to optimize grid size
            if (position === 0) {
                row.splice(hSquares - 1, 1);
            }
            return row;
        });
        return prevMatrix;
    }


    /**
     * The method adds a row to the content motrix at the position specified
     *
     * @param matrix - the content matrix, the input will not be updated
     * @param position - the position at which the row will be added
     * @param hSquares - the current number of columns
     * @param vSquares - the current number of rows
     * @param container - the container ref
     * @returns the content matrix with the new row
     */
    addRowToMatrix(matrix: ReactElement[][], position: number,
                   hSquares: number, vSquares: number, container: Element) {
        let prevMatrix = matrix.slice();

        // Add a row at the end of the content matrix
        let contentsLine = shuffle(this.props.contents);
        let lastRow: ReactElement[] = range(hSquares).map((column: number) =>
            this.squareComponent(contentsLine[column % contentsLine.length]));
        prevMatrix.splice(position, 0, lastRow);

        // We remove the last (or the first) row to optimize the grid size
        if (vSquares >= HOME_CONFIG.MAX_VERTICAL_SQUARES) {
            if (position === 0) {
                prevMatrix.splice(vSquares - 1, 1);
            }
            else {
                prevMatrix.splice(0, 1);
                container.scrollBy(0, -this.SQUARE_SIZE);
            }
        }

        return prevMatrix;
    }


    /**
     * This method check the current user position and, if the user is near the border of the matrix,
     * it adds new rows/columns to the borders so the user never reach the end of the matrix and throws a rerender
     */
    addToBorders(container: Element) {

        // When the grid is rotated we do not add new squares to the borders
        if (this.rotated) return;

        let gridPos: DOMRect = container.children[0].getBoundingClientRect();
        let currX = -gridPos.x;
        let currY = -gridPos.y;

        let matrix = this.state.contentMatrix;
        let hSquares = this.state.hSquares;
        let vSquares = this.state.vSquares;

        let changed = false;

        if (currY < this.SQUARE_SIZE) {
            // The user is near the top of the matrix, therefore we will add a row at the beginning,
            // which will be displayed at the top and we will scroll one square down

            matrix = this.addRowToMatrix(matrix, 0, hSquares, vSquares, container);
            container.scrollBy(0, this.SQUARE_SIZE);
            changed = true;
        }
        else if (currY > (this.state.vSquares - 6) * this.SQUARE_SIZE) {
            // The user is near the bottom of the matrix, threrefore we will add a row at the end

            matrix = this.addRowToMatrix(matrix, vSquares, hSquares, vSquares, container);
            vSquares = Math.min(vSquares + 1, HOME_CONFIG.MAX_VERTICAL_SQUARES);
            changed = true;
        }

        if (currX < this.SQUARE_SIZE) {
            // The user is near the left of the matrix,
            // therefore we will add a column at the beginning and we will scroll one square right

            matrix = this.addColumnToMatrix(matrix, 0, hSquares, vSquares);
            container.scrollBy(this.SQUARE_SIZE, 0);
            changed = true;
        }
        else if (currX > (this.state.hSquares - 8) * this.SQUARE_SIZE) {
            // The user is near the bottom of the matrix, therefore we will add a column at the end
            matrix = this.addColumnToMatrix(matrix, hSquares, hSquares, vSquares);
            hSquares = hSquares + 1;
            changed = true;
        }

        if (changed) {
            // if we have updated the content
            this.setState({
                hSquares: hSquares,
                vSquares: vSquares,
                contentMatrix: matrix
            });
        }
    }


    /**
     * The method scroll the page in the current direction, which is MOVES[this.moveIndex]
     */
    moveTheGrid(container: Element) {
        let moves = this.MOVES[this.moveIndex];
        container.scrollBy(moves[0], moves[1]);
    }

    /**
     * The method change the direction in which the sqaures are moving
     */
    changeDirection() {
        if (this.rotated) return;
        this.moveIndex = (this.moveIndex + Math.floor(Math.random() * 2)) % 8;
    }

    tryInitialization(): void {
        let grid = ReactDOM.findDOMNode(this.gridRef.current) as Element;
        if (!grid) return;



        // If we can find the home grid components and the intervals have never been initialized, we set all of them
        // We can animate the grid only after the first animation has completed
        if (!this.intervalInitialized) {

            this.intervalInitialized = true;

            // Set the initial position in the middle of the screen
            grid.scrollTo(this.state.hSquares * this.SQUARE_SIZE / 2, this.state.vSquares * this.SQUARE_SIZE / 2);


            // Define the recurrent events that will move and expand the grid
            this.moveInterval = setInterval(() => this.moveTheGrid(grid), HOME_CONFIG.MOVE_FREQUENCY_IN_MILLISECONDS); //DEBUG
            this.changeDirectionInterval = setInterval(() => this.changeDirection(), HOME_CONFIG.CHANGE_DIRECTION_FREQUENCY_IN_SECONDS * 1000);
            this.addToBordersInterval = setInterval(() => this.addToBorders(grid), HOME_CONFIG.EXPAND_FREQUENCY_IN_MILLISECONDS);
        }


        // After we have created the first grid and it has been rendered, we make the first animation start if we have to
        if (!this.state.firstAnimationStarted && this.props.firstAnimationStarted) {

            this.setState({
                firstAnimationStarted: true
            });
        }
    }

    componentDidMount(): void {

        if (this.props.contents && !this.state.contentMatrix) {
            this.setState({
                // for each row and column we create (and render) a square component
                contentMatrix: this.firstContentsMatrix(this.state.hSquares, this.state.vSquares)
            });
        }

        this.tryInitialization();

        // If we are on desktop we use a mouse move event listener to understand
        if (!isMobile) {
            document.onmousemove = (e => {
                let y = e.clientY;

                // If the mouse is near to the latest update's position or is far from the border, we don't do anything
                if (Math.abs(y - this.prevY) < HOME_CONFIG.MIN_MOUSE_DELTA_TO_ROTATE || (y > 150 && !this.rotated)) return;

                let gridRef = (ReactDOM.findDOMNode(this.gridRef.current) as Element);
                let fadeRef = (ReactDOM.findDOMNode(this.fadeRef.current) as Element);

                if (!gridRef || !fadeRef) return;

                this.prevY = y;
                if (y < 150) {
                    this.rotated = true;

                    if (this.moveIndex !== DOWNWARD_DIRECTION_INDEX) {
                        this.moveIndex = DOWNWARD_DIRECTION_INDEX;
                    }

                    gridRef.setAttribute("style",
                        "transform: perspective(320px) rotateX(" + ((150.0 - y) / 150.0 * HOME_CONFIG.MAX_GRID_INCLINATION_DEG) + "deg);");
                    fadeRef.setAttribute("style",
                        "opacity: " + ((150.0 - y) / 75.0) + "; top: " + ((150.0 - y) / 25.0 * HOME_CONFIG.MAX_GRID_INCLINATION_DEG) + "px; display:block;");
                } else {
                    this.rotated = false;
                    gridRef.setAttribute("style", "");
                    fadeRef.setAttribute("style", "opacity: 0; top: 0; display: none;");
                }
            });
        }
    }


    firstContentsMatrix(hSquares: number, vSquares: number) {

        return range(vSquares).map((row: number) => {
            let length = this.props.contents.length;
            let subarrays = [
                shuffle(this.props.contents.slice(0, length/3)),
                shuffle(this.props.contents.slice(length/3, length*2/3)),
                shuffle(this.props.contents.slice(length*2/3))
            ];

            let contentsRow = subarrays[row % 3].concat(subarrays[(row + 1) % 3]).concat(subarrays[(row + 2) % 3]);

            return range(hSquares).map((column: number) => {

                let hDistance = - HOME_CONFIG.MAX_FIRST_ANIMATION_H_DELTA_PX + 2 * HOME_CONFIG.MAX_FIRST_ANIMATION_H_DELTA_PX * Math.random();
                let vDistance = - HOME_CONFIG.MAX_FIRST_ANIMATION_V_DELTA_PX + 2 * HOME_CONFIG.MAX_FIRST_ANIMATION_V_DELTA_PX * Math.random();
                let squareSize = this.SQUARE_SIZE * (0.5 + 0.5 * Math.random());
                return this.squareComponent(contentsRow[column % this.props.contents.length], hDistance, vDistance, squareSize);
            });
        });
    }

    componentDidUpdate(prevProps: Readonly<HomeGridProps>, prevState: Readonly<any>, snapshot?: any): void {

        // If the contentMatrix has never been initialized and we have our contents, we create the grid
        if (this.props.contents && (!this.state.contentMatrix || this.props.contents !== prevProps.contents)) {

            this.setState({
                // for each row and column we create (and render) a square component
                contentMatrix: this.firstContentsMatrix(this.state.hSquares, this.state.vSquares)
            });
        }

        this.tryInitialization();

    }

    /**
     * This method is called when the component is going to be unmounted,
     * so we can clear all the resources, which for the Home Grid are only the interval and the state
     */
    componentWillUnmount() {
        // Clear all the intervals when the component unmount
        if (this.moveInterval) {
            clearInterval(this.moveInterval);
        }
        if (this.changeDirectionInterval) {
            clearInterval(this.changeDirectionInterval);
        }
        if (this.addToBordersInterval) {
            clearInterval(this.addToBordersInterval);
        }
    }
    // endregion


    // region: rendering -----------------------------------------------------------------------------------------------

    newKeySuffix(content: Content): string {
        return hashCode((content && content.title) ? content.title : '') + "_" + this.newKey++;
    }

    /**
     *  It renders a HomeSquare component inside the grid
     *  and assigns it a key depending on the row index and the column index of the square in the grid
     *
     * @param content - the content to render inside the square
     * @param xDistance - the initial horizontal distance of the square from its position
     * @param yDistance - the initial vertical distance of the square from its position
     * @param squareSize - the initial size of the square
     * @returns the tags to render
     */
    squareComponent(content: Content, xDistance: number = 0, yDistance: number = 0, squareSize: number = -1): ReactElement {

        let keySuffix = this.newKeySuffix(content);
        let style = {};

        if (xDistance !== 0 && yDistance !== 0 && squareSize >= 0) {
            style = {
                marginLeft: xDistance,
                marginTop: yDistance,
                width: squareSize,
                height: squareSize
            }
        }


        return (
            <GridListTile key={"tile" + keySuffix} className="home-tile">
                <HomeSquare key={"square" + keySuffix} content={content} style={style}
                            onSquareSelected={() => this.props.squareSelected(content)} />
            </GridListTile>
        );
    }

    render() {

        // Return until we have downloaded the contents to be displayed
        if (!this.state.contentMatrix) return null;

        let gridSize = {
            width: this.state.hSquares * this.SQUARE_SIZE,
            height: this.state.vSquares * this.SQUARE_SIZE
        };


        return (
            <div>
                <div className="grid-rotation-fade" ref={this.fadeRef} />
                <div ref={this.rootRef} className={`home-grid-root ${this.state.firstAnimationStarted ? "first-animation-started" : ""}`}
                    id="home-grid-root">
                    <ScrollContainer className="scroll-container scrollable" horizontal={true} vertical={true}
                                     ref={this.gridRef} nativeMobileScroll={true} activationDistance={20}
                                    ignoreElements=".home-tile *">
                        <GridList spacing={0} cols={this.state.hSquares} id="home-scroll-container"
                                  cellHeight={this.SQUARE_SIZE} style={ gridSize }>
                            { this.state.contentMatrix }
                        </GridList>
                    </ScrollContainer>
                </div>
            </div>
        );
    }
    // endregion -------------------------------------------------------------------------------------------------------
}

export default HomeGrid;

